import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { logger } from './logger';
import { createSelectors } from './selectors';
import type { AuthActions, AuthState } from '@/types/auth.types';

const AUTH_STORE_NAME = 'auth.store';

const DEFAULT_FB_CONFIG = {
  appId: '1116887643631810',
  version: 'v20.0',
};

const authStore = create<
  AuthState & AuthActions,
  [
    ['zustand/devtools', AuthState & AuthActions],
    [
      'zustand/persist',
      Pick<AuthState, 'user' | 'facebookAppId' | 'facebookAppVersion'>,
    ],
  ]
>(
  logger(
    devtools(
      persist(
        (set, get) => {
          const initialState: AuthState = {
            user: null,
            isAuthenticated: false,
            isLoading: false,
            facebookAppId: DEFAULT_FB_CONFIG.appId,
            facebookAppVersion: DEFAULT_FB_CONFIG.version,
          };

          return {
            ...initialState,
            setUser: (user) =>
              set({
                user,
                isAuthenticated: !!user,
              }),
            setIsLoading: (isLoading) => set({ isLoading }),
            setFacebookConfig: (appId, version = DEFAULT_FB_CONFIG.version) =>
              set({
                facebookAppId: appId,
                facebookAppVersion: version,
              }),
            login: (authResponse, userInfo) => {
              const user = {
                id: authResponse.userID,
                name: userInfo.name,
                email: userInfo.email,
                picture: userInfo.picture?.data?.url,
                accessToken: authResponse.accessToken,
                provider: 'facebook' as const,
                expiresIn: authResponse.expiresIn,
                loginTime: Date.now(),
                cunstomerId: authResponse.userID, // Added required cunstomerId property
              };
              set({
                user,
                isAuthenticated: true,
                isLoading: false,
              });
            },
            setSystemToken: (systemToken) => {
              const { user } = get();
              if (user) {
                // Tính thời gian hết hạn 3 tháng (90 ngày)
                const now = Date.now();
                const threeMonthsInMs = 90 * 24 * 60 * 60 * 1000; // 90 ngày
                const systemTokenExpiry = now + threeMonthsInMs;
                set({
                  user: {
                    ...user,
                    systemToken,
                    systemTokenExpiry,
                  },
                });
              }
            },
            logout: () => {
              // Don't delete cookie here, let auth-provider handle it
              set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
              });
            },
            checkAuthStatus: () => {
              const { user } = get();
              if (!user?.systemTokenExpiry) {
                set({ user: null, isAuthenticated: false });
                return;
              }
              const now = Date.now();
              if (now < user.systemTokenExpiry) {
                set({ isAuthenticated: true });
              } else {
                set({ user: null, isAuthenticated: false });
              }
            },
            isTokenExpired: () => {
              const { user } = get();
              if (!user) return true;
              const now = Date.now();
              const tokenExpiryTime = user.loginTime + user.expiresIn * 1000;
              return now >= tokenExpiryTime;
            },
            isSystemTokenExpired: () => {
              const { user } = get();
              if (!user?.systemToken || !user.systemTokenExpiry) return true;
              const now = Date.now();
              return now >= user.systemTokenExpiry;
            },
          };
        },
        {
          name: AUTH_STORE_NAME,
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({
            user: state.user,
            facebookAppId: state.facebookAppId,
            facebookAppVersion: state.facebookAppVersion,
          }),
          onRehydrateStorage: () => (state) => {
            // Validate token and set authentication status on rehydration
            if (state?.user?.systemToken && state.user.systemTokenExpiry) {
              const now = Date.now();
              const isTokenValid = now < state.user.systemTokenExpiry;
              state.isAuthenticated = isTokenValid;

              // If token is expired, clear user data
              if (!isTokenValid) {
                state.user = null;
                state.isAuthenticated = false;
              }
            } else if (state) {
              state.isAuthenticated = false;
            }
          },
        }
      ),
      {
        name: AUTH_STORE_NAME,
      }
    ),
    AUTH_STORE_NAME
  )
);

export const useAuthStore = createSelectors(authStore);
